import {
  ProblemDetailsSchema,
  RULE_LABELS,
  type BusinessRule,
  type Conflict,
  type ProblemDetails,
} from '@swi/contracts';

/**
 * An API failure, already parsed into the shared ProblemDetails shape.
 *
 * The UI never inspects an untyped exception: whatever the API returns is run
 * through ProblemDetailsSchema, so `violatedRule` is a known constant and the
 * message comes from the same RULE_LABELS the backend keys its errors with.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get violatedRule(): BusinessRule | undefined {
    return this.problem?.violatedRule;
  }

  get conflicts(): Conflict[] {
    return this.problem?.conflicts ?? [];
  }

  get fieldErrors(): Record<string, string[]> {
    return this.problem?.errors ?? {};
  }

  /** What to show the user: the rule label when a rule was broken, else the detail. */
  get humanMessage(): string {
    if (this.problem?.violatedRule) {
      return RULE_LABELS[this.problem.violatedRule];
    }
    if (this.status === 401) {
      return 'Přihlášení vypršelo. Přihlas se prosím znovu.';
    }
    if (this.status === 403) {
      return 'Na tuto operaci nemáš oprávnění.';
    }
    if (this.status === 404) {
      return 'Požadovaný záznam neexistuje.';
    }
    return this.problem?.detail ?? this.message;
  }

  get technicalDetail(): string | undefined {
    const violation = this.problem?.violations?.find(
      (candidate) => candidate.rule === this.problem?.violatedRule,
    );
    return violation?.detail ?? this.problem?.detail;
  }
}

export function toApiError(status: number, body: unknown): ApiError {
  const parsed = ProblemDetailsSchema.safeParse(body);
  if (parsed.success) {
    return new ApiError(status, parsed.data, parsed.data.title);
  }
  return new ApiError(status, null, `Request failed with status ${String(status)}`);
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;
